/* eslint-disable no-param-reassign */
import { ComboboxItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import useCancerConfig from '@oncolens/oncolens-ui/lib/hooks/config/useCancerConfig';
import useConfig from '@oncolens/oncolens-ui/lib/hooks/config/useConfig';
import { FormStoreProps } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import { useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useParams } from 'react-router';
import { useOrganizationData } from '../organizations/organization.hooks';
import {
  BehaviorConfigDto,
  BehaviorConfigFormValues,
  useBehaviorConfigById,
  useUserSpecialtiesData,
} from './api/behavior-configs';
import { mapLocations, mapPathologies } from './utils/behavior-config';

function useMappedConfigs() {
  const { data: cancerConfig } = useCancerConfig();

  return useMemo(
    () => ({
      pathologies: mapPathologies(cancerConfig),
      locations: mapLocations(cancerConfig),
    }),
    [cancerConfig],
  );
}

function mapSelectedValues(values: string[], source: ComboboxItem[] = []): ComboboxItem[] | null {
  if (!values || !source || !source.length) return null;
  return source.filter((x) => values.some((y) => y === x.value));
}
function useHydrateBehaviorConfig(
  updateStore: (fn: (state: FormStoreProps<BehaviorConfigFormValues>) => void) => void,
) {
  const { configId } = useParams();
  const orgData = useOrganizationData();
  const { pathologies, locations } = useMappedConfigs();
  const userSpecialties = useUserSpecialtiesData();
  const { data: settingData } = useConfig('setting');

  useBehaviorConfigById(configId || '', {
    enabled:
      !!configId && !!orgData && !!pathologies && !!locations && !!settingData && !!userSpecialties,
    onSuccess: (data: BehaviorConfigDto) => {
      updateStore((x) => {
        Object.assign(x, data);
        x.dateRangeFilters = {
          startDate: data && data.startDate ? new Date(data.startDate) : undefined,
          endDate: data && data.endDate ? new Date(data.endDate) : undefined,
        };

        x.organizationIds = mapSelectedValues(data.organizationIds, orgData);
        x.diseaseTypeCriteria = mapSelectedValues(data.diseaseTypeCriteria, pathologies);
        x.diseaseLocationCriteria = mapSelectedValues(data.diseaseLocationCriteria, locations);
        x.userSpecialties = mapSelectedValues(data.userSpecialties, userSpecialties);
        x.diseaseSettingCriteria = mapSelectedValues(data.diseaseSettingCriteria, settingData);
      });
    },
  });
  return { orgData, configId, pathologies, locations, userSpecialties, settingData };
}

export { useMappedConfigs, useOrganizationData, useHydrateBehaviorConfig };
